import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, X, User, Home } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STAGE = (s) => (s === 'legacy_pending_review' ? 'Legacy (to review)' : s);

const chip = (on) => `px-2 py-0.5 rounded-full border text-[11px] transition-colors ${on ? 'bg-[#0F4F1B] text-white border-[#0F4F1B]' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`;

// Step 1 — recent jobs listed straight away; search, or click a client / address / quote type to shortlist.
export const JobFinder = ({ onPick, clientId, propertyId }) => {
  const { api } = useAuth();
  const [q, setQ] = useState('');
  const [f, setF] = useState({ client_id: clientId || '', property_id: propertyId || '', status: '', quote_type: '', label: '' });
  const [opts, setOpts] = useState({ statuses: [], quote_types: [] });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const reqId = useRef(0);
  const [err, setErr] = useState('');
  const run = async (query = q, filt = f) => {
    const my = ++reqId.current;
    setBusy(true); setErr('');
    const p = { recent: 1, ...(query ? { q: query } : {}) };
    ['client_id', 'property_id', 'status', 'quote_type'].forEach((k) => { if (filt[k]) p[k] = filt[k]; });
    try {
      const { data } = await api.get('/update-resend/find', { params: p });
      if (my === reqId.current) setRows(data.rows || []);  // ignore stale responses
    } catch (e) { if (my === reqId.current) setErr(e?.response?.data?.detail || 'Search failed — try again.'); }
    finally { if (my === reqId.current) setBusy(false); }
  };
  useEffect(() => { api.get('/update-resend/filters').then(({ data }) => setOpts(data)).catch(() => {}); }, [api]);
  useEffect(() => { run(q, f); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (patch) => setF((x) => ({ ...x, ...patch }));
  const clearWho = () => setFilter({ client_id: '', property_id: '', label: '' });
  const anyFilter = f.client_id || f.property_id || f.status || f.quote_type || q;

  return (
    <div className="bg-white border border-[#E2E2DF] rounded-md p-4 space-y-3" data-testid="ur-finder">
      <div className="flex gap-2">
        <input data-testid="ur-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="A-number, client name, phone, email or street…" className="flex-1 px-3 py-2 text-sm border border-[#E2E2DF] rounded focus:outline-none focus:ring-2 focus:ring-[#0F4F1B]/30" />
        <button data-testid="ur-search-btn" onClick={() => run()} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3c14]">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Find job
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="ur-filters">
        <select data-testid="ur-filter-status" value={f.status} onChange={(e) => setFilter({ status: e.target.value })} className="px-2 py-1 border border-slate-300 rounded">
          <option value="">Any stage</option>{opts.statuses.map((s) => <option key={s} value={s}>{STAGE(s)}</option>)}
        </select>
        <select data-testid="ur-filter-quote-type" value={f.quote_type} onChange={(e) => setFilter({ quote_type: e.target.value })} className="px-2 py-1 border border-slate-300 rounded max-w-[260px]">
          <option value="">Any quote / build type</option>{opts.quote_types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {f.label && (
          <span className={chip(true)} data-testid="ur-filter-who">{f.label}
            <button onClick={clearWho} className="ml-1" data-testid="ur-filter-who-clear"><X size={10} className="inline" /></button></span>
        )}
        {anyFilter && <button data-testid="ur-filter-clear-all" onClick={() => { setQ(''); setF({ client_id: '', property_id: '', status: '', quote_type: '', label: '' }); }} className="text-slate-500 underline">Clear all</button>}
        <span className="ml-auto text-slate-400" data-testid="ur-result-label">{anyFilter ? `${rows.length} match${rows.length === 1 ? '' : 'es'}` : 'Most recent jobs'}</span>
      </div>
      <div className="divide-y divide-[#F1EFEC]">
        {rows.map((r) => (
          <div key={r.id} data-testid={`ur-job-row-${r.appraisal_no}`} className="grid grid-cols-[80px_1fr_1fr_130px_90px] gap-2 py-2 px-1 text-xs hover:bg-slate-50 items-start">
            <button data-testid={`ur-job-${r.appraisal_no}`} onClick={() => onPick(r.id)} className="text-left font-mono font-semibold text-[#0F4F1B] hover:underline">A{r.appraisal_no}</button>
            <button data-testid={`ur-job-client-${r.appraisal_no}`} title="Show only this client's jobs" onClick={() => setFilter({ client_id: r.client_id, property_id: '', label: r.client_name })} className="text-left hover:underline inline-flex items-center gap-1"><User size={11} className="text-slate-400" />{r.client_name || '—'}</button>
            <div>
              <button data-testid={`ur-job-address-${r.appraisal_no}`} title="Show only this property's jobs" onClick={() => setFilter({ property_id: r.property_id, client_id: '', label: r.address })} className="text-left text-slate-600 hover:underline inline-flex items-center gap-1"><Home size={11} className="text-slate-400" />{r.address || '—'}</button>
              {r.types?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {r.types.map((t, i) => (
                    <button key={i} data-testid={`ur-job-type-${r.appraisal_no}-${i}`} onClick={() => setFilter({ quote_type: t.quote_type })} title="Show only jobs with this quote type"
                      className={chip(f.quote_type === t.quote_type)}>{t.appraisal_no ? `A${t.appraisal_no} · ` : ''}{t.quote_type}{t.variant ? ` · ${t.variant}` : ''}</button>
                  ))}
                </div>
              )}
            </div>
            <button data-testid={`ur-job-status-${r.appraisal_no}`} onClick={() => setFilter({ status: r.status })} className="text-left text-slate-500 hover:underline">{STAGE(r.status)}{r.date ? <span className="block text-[10px] text-slate-400">{r.date}</span> : null}</button>
            <button onClick={() => onPick(r.id)} data-testid={`ur-job-open-${r.appraisal_no}`} className="text-right text-[#0F4F1B] font-semibold hover:underline">{r.quotes ? `${r.quotes} quote${r.quotes === 1 ? '' : 's'}` : 'single'} →</button>
          </div>
        ))}
        {err && <div className="text-xs text-rose-700 py-2" data-testid="ur-find-error">{err}</div>}
        {!busy && !err && rows.length === 0 && <div className="text-xs text-slate-400 py-2">No jobs match — try another search or clear the filters.</div>}
      </div>
    </div>
  );
};
